import { FieldInput } from 'sr-types/lib/documenttemplate/v1/graphql';
import {
    AlternateIdInput,
    Contact,
    ContactInfo,
    ContactInfoInput,
    ContactInput,
    CreativeCreditInput,
    DateRangeInput,
    DeiInfoInput,
    EmailInput,
    Experience,
    LocationInput,
    MembershipInput,
    NameInput,
    Person,
    PersonBasicProfileInput,
    PhoneInput,
    ReferenceInput,
    SummaryInput,
    useSavePersonWithBasicProfileMutation
} from 'sr-types/lib/person/v1/graphql';
import { FieldDefinition } from 'sr-types/lib/search/v1/graphql';
import { Attribute } from '../../../attributes/attributes';
import { client } from '../../../common/components/person/person';

import { cloneDeep, isNil } from 'lodash';
import { Dispatch, SetStateAction, useContext } from 'react';
import { UserContext } from '../../../common/auth/UserContext';
import { addAlternateId, findAlternateId } from '../../../common/components/utils/alternateIdFormUtils';
import { IMDB_LABEL, LINKEDIN_LABEL, STUDIO_SYSTEM_LABEL } from '../../../common/constants/alternateIdConstants';
import { Reference } from '../../../common/reference/reference';
import { failedToastMessage, successToastMessage } from '../../../common/utils/commonUtils';
import { getFullName, PERSON_EMPTY_LAST_NAME } from '../../person';
import { AlternateIdProps } from './personAlternateIds';

export interface CommonExtendedFields {
    Imdb: string;
    LinkedIn: string;
    StudioSystem: string;
    memberships: ReferenceInput[];
    imdbAlternateId: AlternateIdInput;
    linkedInAlternateId: AlternateIdInput;
    studioSystemAlternateId: AlternateIdInput;
    imdbProfileId: string;
    linkedInProfileId: string;
    genderCustom: string;
    ethnicityCustom: string;
    disabilityStatusDescription: string;
    genre?: Array<string>;
    technicalExpertise?: Array<string>;
    language?: Array<string>;
    inquireOptions?: string;
    isSharedInDirectory?: boolean;
    organizationReference?: Reference;
    title?: string;
    department?: string;
    professionalRoles?: Array<string>;
    showReference?: Reference;
    creativeCredits?: {
        department?: string;
    };
    role?: Array<string>;
}

export interface BasicInfoInputExtended extends Omit<PersonBasicProfileInput, 'memberships'>, CommonExtendedFields {}

export interface BasicInfoFormItemsProps extends AlternateIdProps {
    fieldDefinitionsData: FieldDefinition[];
    onEditSkills: () => void;
    attributes?: Attribute[];
    setAttributes?: Dispatch<SetStateAction<Attribute[]>>;
    setResumeReference?: Dispatch<SetStateAction<Reference>>;
    editFieldName?: string;
}

export const EMPTY_STATE = {
    memberships: [],
    associatedFieldDefinitionReferences: [],
    customFields: [],
    genre: [],
    language: [],
    technicalExpertise: [],
    Imdb: '',
    LinkedIn: '',
    StudioSystem: '',
    imdbAlternateId: undefined,
    linkedInAlternateId: undefined,
    studioSystemAlternateId: undefined,
    imdbProfileId: undefined,
    linkedInProfileId: undefined,
    genderCustom: undefined,
    ethnicityCustom: undefined,
    disabilityStatusDescription: undefined,
    isSharedInDirectory: false,
    businessContacts: [],
    name: {
        firstName: '',
        lastName: ''
    }
};

export const OTHER = 'Other';
export const YES = 'Yes';

export const MUI_DATE_MONTH_AND_DAY = '0101';

export const stateToPersonBasicInfoInput = (personBasicInfo) => {
    const {
        contactInfo,
        Imdb,
        imdbAlternateId,
        linkedInAlternateId,
        StudioSystem,
        studioSystemAlternateId,
        LinkedIn,
        imdbProfileId,
        linkedInProfileId,
        deiInfo,
        genderCustom,
        ethnicityCustom,
        disabilityStatusDescription,
        memberships,
        summary,
        genre,
        technicalExpertise,
        language,
        associatedFieldDefinitionReferences,
        customFields,
        initialValues,
        profileImage,
        inquireOptions,
        isSharedInDirectory,
        businessContacts,
        ...state
    } = personBasicInfo;

    const contactInfoDestructured: ContactInfoInput = constructContactInfo(personBasicInfo?.contactInfo);

    const deiInfoDestructured: DeiInfoInput =
        deiInfo && handleDeiInfoValuesToInput(deiInfo, disabilityStatusDescription, ethnicityCustom, genderCustom);

    const constructedAlternateIds = [];
    imdbAlternateId && addAlternateId(constructedAlternateIds, imdbAlternateId, imdbProfileId, IMDB_LABEL);
    linkedInAlternateId &&
        addAlternateId(constructedAlternateIds, linkedInAlternateId, linkedInProfileId, LINKEDIN_LABEL);
    studioSystemAlternateId &&
        addAlternateId(constructedAlternateIds, studioSystemAlternateId, '', STUDIO_SYSTEM_LABEL);

    const membershipObjects = memberships && createMembershipObject(memberships);
    const summaryObject = summary && constructSummary(summary.headline, summary.about);
    const profileImageReference = {
        id: profileImage || '',
        label: ''
    };

    const isPersonSharedInDirectory = inquireOptions === 'includeInquire';
    const businessContactsInput: ContactInfo[] = businessContacts?.map((bc) => {
        const { profileImage, contactInfo, ...businessContactInput } = bc;
        const profileImageReference = { id: profileImage || '' };
        const contactInfoInput = constructContactInfo(contactInfo);
        return { profileImageReference, contactInfo: contactInfoInput, ...businessContactInput };
    });

    state?.security && delete state.security;
    return {
        contactInfo: contactInfoDestructured,
        alternateIds: constructedAlternateIds,
        deiInfo: deiInfoDestructured,
        memberships: membershipObjects,
        summary: summaryObject,
        associatedFieldDefinitionReferences,
        customFields,
        profileImageReference,
        isSharedInDirectory: isPersonSharedInDirectory,
        businessContacts: businessContactsInput,
        ...state
    };
};

export const constructContactInfo = (contactInfo: ContactInfoInput) => {
    return {
        email:
            contactInfo && contactInfo.email && contactInfo.email.address
                ? {
                      address: contactInfo.email.address,
                      typeString: 'Business',
                      verified: false
                  }
                : null,
        phone:
            contactInfo && contactInfo.phone && contactInfo.phone.number
                ? {
                      number: contactInfo.phone.number,
                      typeString: 'Mobile'
                  }
                : null
    };
};

export const handleDeiInfoValuesToInput = (
    deiInfo: DeiInfoInput,
    disabilityStatusDescription: string,
    ethnicityCustom: string,
    genderCustom: string
) => {
    const { birthYear, disabilityStatus, ethnicity, gender, socioEconomicStatus } = deiInfo;
    const deiInfoDestructured: DeiInfoInput = {
        birthYear: (birthYear && birthYear.substring(0, 4)) || '',
        disabilityStatus: disabilityStatus === YES ? disabilityStatusDescription : disabilityStatus || '',
        ethnicity: ethnicity === OTHER ? ethnicityCustom : ethnicity || '',
        gender: gender == OTHER ? genderCustom : gender || '',
        socioEconomicStatus: socioEconomicStatus || ''
    };
    return deiInfoDestructured;
};

export const createMembershipObject = (memberships: Array<Reference | ReferenceInput>) => {
    return memberships && memberships.map((m) => ({ organizationReference: m }));
};

export const constructSummary = (headline: string = '', about: string = '') => {
    return { headline, about };
};

export const updatePersonBasicInfo = (state, onClose, onSuccess, updatePersonInfo) => {
    const personBasicInfoInput = stateToPersonBasicInfoInput(state);

    return updatePersonInfo({
        input: {
            ...personBasicInfoInput
        }
    })
        .then((id) => {
            if (id) {
                onSuccess(id);
                return { id };
            } else {
                failedToastMessage('person.save.error');
                return null;
            }
        })
        .catch(() => {
            failedToastMessage('person.save.error');
            return null;
        })
        .finally(onClose);
};

export const extractedBasicPersonData = (personBasicInfo) => {
    const { name, contactInfo: personContactInfo, identity } = personBasicInfo;
    const contactInfo = constructContactInfo(personContactInfo);
    return { name, contactInfo, identity };
};

export const addPersonBasicInformation = async (
    state,
    setState,
    updatePersonInfo,
    updatePersonProfessionalExperience,
    updateCreativeCredits,
    changeRouteAfterSavingPerson
) => {
    const personBasicInfoInput = extractedBasicPersonData(state);

    let personId;
    try {
        personId = await updatePersonInfo({
            input: {
                ...personBasicInfoInput
            }
        });

        if (personId) {
            const [experienceResult, creativeCreditsResult] = await Promise.all([
                updatePersonProfessionalExperience(personId),
                updateCreativeCredits(personId)
            ]);

            const experienceErrors = experienceResult?.data?.saveExperienceToPerson?.errors;
            const creativeCreditsErrors = creativeCreditsResult?.data?.saveCreativeCreditToPerson?.errors;

            if (experienceErrors || creativeCreditsErrors) {
                throw new Error(experienceErrors || creativeCreditsErrors);
            }
            successToastMessage('person.save.success', { person: getFullName(state.name) });
            setState(cloneDeep(EMPTY_STATE));
            changeRouteAfterSavingPerson(personId);
            return personId;
        } else {
            throw new Error('Person ID is undefined');
        }
    } catch (error) {
        failedToastMessage('person.save.error');
        return null;
    }
};

export const useUpdatePersonBasicInfo = () => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const [savePersonWithBasicProfileMutation, { loading: basicInfoSaving }] = useSavePersonWithBasicProfileMutation({
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        client
    });

    const updatePersonInfo = ({ input }) => {
        return new Promise<any>((resolve, reject) => {
            savePersonWithBasicProfileMutation({
                variables: {
                    input
                }
            })
                .then((res) => {
                    res.data.savePersonWithBasicProfile.errors
                        ? reject()
                        : resolve(res.data.savePersonWithBasicProfile.id);
                })
                .catch(() => {
                    reject();
                });
        });
    };
    return { updatePersonInfo, basicInfoSaving };
};

export const experienceToInput = (experience) => {
    const { dateRange, organizationReference, ...state } = experience;
    delete state.__typename;

    const dateRangeDestructured: DateRangeInput = dateRange.start !== '' && dateRange.end !== '' ? dateRange : null;
    const organizationReferenceDestructured: ReferenceInput = experience.organizationReference || {
        label: state.experienceName
    };

    return { dateRange: dateRangeDestructured, organizationReference: organizationReferenceDestructured, ...state };
};

export const getBusinessContactOrganizationPreviewFields = (
    contact: Pick<Contact, 'organization' | 'organizationReference'>
): Pick<ContactInput, 'organization' | 'organizationReference'> => {
    const organizationReferenceLabel = contact.organizationReference?.label?.trim();
    const organization = organizationReferenceLabel || contact.organization?.trim();

    return {
        organization,
        organizationReference: contact.organizationReference
            ? {
                  ...contact.organizationReference,
                  label: organizationReferenceLabel || organization || ''
              }
            : contact.organizationReference
    };
};

export const extractPersonBasicInfoForPreview = (person: Person): BasicInfoInputExtended => {
    const {
        availableToHire,
        contactInfo,
        identity,
        location,
        name,
        professionalRoles,
        profileAttributes,
        profileImage,
        summary,
        alternateIds,
        deiInfo,
        memberships,
        associatedFieldDefinitionReferences,
        customFields,
        isSharedInDirectory,
        businessContacts,
        resumeReference,
        security
    } = person;

    const imdb = alternateIds?.length && findAlternateId(alternateIds, IMDB_LABEL);
    const linkedIn = alternateIds?.length && findAlternateId(alternateIds, LINKEDIN_LABEL);
    const studioSystem = alternateIds?.length && findAlternateId(alternateIds, STUDIO_SYSTEM_LABEL);

    const membershipsWithReferencesExtracted: ReferenceInput[] = memberships?.map(
        (m: MembershipInput) => m.organizationReference
    );

    const emailInput: EmailInput = {
        address: contactInfo?.email?.address || null,
        typeString: contactInfo?.email?.typeString || null,
        verified: contactInfo?.email?.verified || false
    };

    const phoneInput: PhoneInput = contactInfo?.phone?.number
        ? {
              number: contactInfo.phone.number,
              typeString: contactInfo.phone.typeString || ''
          }
        : null;

    const customFieldsInput: FieldInput[] =
        customFields?.length &&
        customFields.map(({ fieldDefinitionReference, fieldType, name, value }) => ({
            fieldDefinitionReference,
            fieldType,
            name,
            value
        }));

    let locationInput: LocationInput = null;
    if (location) {
        const { geoCoords, ...rest } = location;
        const lat = geoCoords?.lat;
        const lon = geoCoords?.lon;

        locationInput = {
            ...rest,
            ...(!isNil(lat) && !isNil(lon) ? { geoCoords: { lat, lon } } : {}),
            primary: location.primary || false
        };
    }

    const nameInput: NameInput = {
        ...name,
        firstName: name?.firstName || '',
        lastName: name?.lastName === PERSON_EMPTY_LAST_NAME ? '' : name?.lastName || ''
    };

    const summaryInput: SummaryInput = {
        about: summary?.about || '',
        headline: summary?.headline || ''
    };

    const businessContactsInput: ContactInput[] = businessContacts?.map((contact: Contact) => ({
        profileImage: contact.profileImageReference?.id,
        role: contact.role,
        ...getBusinessContactOrganizationPreviewFields(contact),
        contactInfo: contact.contactInfo as ContactInfoInput,
        name: contact.name as NameInput,
        isPrimary: contact.isPrimary,
        personReference: contact.personReference
    }));

    const personBasicInfo = {
        availableToHire,
        contactInfo: {
            email: emailInput,
            phone: phoneInput
        },
        identity,
        location: locationInput,
        name: nameInput,
        professionalRoles,
        profileAttributes,
        profileImage: profileImage || '',
        summary: summaryInput,
        Imdb: imdb?.url,
        StudioSystem: studioSystem?.url || '',
        LinkedIn: linkedIn?.url,
        imdbAlternateId: imdb,
        linkedInAlternateId: linkedIn,
        studioSystemAlternateId: studioSystem,
        deiInfo: deiInfo?.birthYear
            ? { ...deiInfo, birthYear: deiInfo.birthYear + MUI_DATE_MONTH_AND_DAY }
            : { ...deiInfo },
        memberships: memberships ? membershipsWithReferencesExtracted : [],
        imdbProfileId: undefined,
        linkedInProfileId: undefined,
        genderCustom: undefined,
        ethnicityCustom: undefined,
        disabilityStatusDescription: undefined,
        associatedFieldDefinitionReferences,
        customFields: customFieldsInput,
        isSharedInDirectory,
        businessContacts: businessContactsInput || [],
        resumeReference,
        security
    };
    return personBasicInfo;
};

export const removeEmptyCreativeCredits = (creativeCredits: CreativeCreditInput[]) => {
    return creativeCredits.filter((c) => !isCreativeCreditObjectEmpty(c));
};

export const removeEmptyExperiences = (experiences: Experience[]) => {
    return experiences.filter((e) => !isExperienceObjectEmpty(e));
};

const isExperienceObjectEmpty = (experience: Experience) => {
    return (
        experience &&
        experience.location === null &&
        experience.experienceName === '' &&
        experience.organizationReference === null &&
        experience.jobType === '' &&
        experience.dateRange.start === '' &&
        experience.dateRange.end === '' &&
        experience.keyExperience === false &&
        (experience.professionalRoles === null ||
            (experience.professionalRoles?.length && experience.professionalRoles[0] === '')) &&
        experience.title === '' &&
        experience.department === ''
    );
};

const isCreativeCreditObjectEmpty = (creativeCredit: CreativeCreditInput) => {
    return (
        creativeCredit &&
        creativeCredit.location === null &&
        creativeCredit.showName === '' &&
        creativeCredit.showType === '' &&
        creativeCredit.showReference === null &&
        creativeCredit.dateRange.start === '' &&
        creativeCredit.dateRange.end === '' &&
        creativeCredit.keyCredit === false &&
        (creativeCredit.professionalRoles === null ||
            (creativeCredit.professionalRoles?.length && creativeCredit.professionalRoles[0] === '')) &&
        creativeCredit.department === ''
    );
};

export const organizationMembershipsFilters = {
    expressions: [
        {
            field: 'organizationTypes',
            value: {
                values: ['Guild', 'Union']
            }
        },
        { field: 'isSharedInDirectory', value: { values: ['true'] } }
    ]
};
